"use client"

export const dynamic = "force-dynamic"

import { useState, useEffect, useCallback } from "react"
import Link from "next/link"
import { Search, Filter, Building2, MapPin, Tag, ChevronLeft, ChevronRight, DollarSign } from "lucide-react"

interface Award {
  id: string
  usaspendingAwardId: string
  description: string | null
  awardType: string | null
  awardingAgencyName: string | null
  naicsCode: string | null
  obligatedAmount: number | null
  startDate: string | null
  endDate: string | null
  popCity: string | null
  popState: string | null
  lastSyncedAt: string
  recipient: {
    id: string
    name: string
    ueiSam: string | null
    state: string | null
  } | null
}

interface ApiResponse {
  awards: Award[]
  total: number
  page: number
  totalPages: number
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function fmtMoney(n: number | null): string {
  if (n == null) return "—"
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`
  return `$${n.toFixed(0)}`
}

export default function AwardsPage() {
  const [data, setData] = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const [q, setQ] = useState("")
  const [recipient, setRecipient] = useState("")
  const [agency, setAgency] = useState("")
  const [naics, setNaics] = useState("")
  const [popState, setPopState] = useState("")
  const [page, setPage] = useState(1)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" })
      if (q) params.set("q", q)
      if (recipient) params.set("recipient", recipient)
      if (agency) params.set("agency", agency)
      if (naics) params.set("naics", naics)
      if (popState) params.set("popState", popState)

      const res = await fetch(`/api/gov/awards?${params}`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setData(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load awards")
    } finally {
      setLoading(false)
    }
  }, [q, recipient, agency, naics, popState, page])

  useEffect(() => { fetchData() }, [fetchData])

  function applyFilters(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    fetchData()
  }

  function clearFilters() {
    setQ(""); setRecipient(""); setAgency(""); setNaics(""); setPopState(""); setPage(1)
  }

  const hasFilters = q || recipient || agency || naics || popState

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Award Intelligence</h1>
        <p className="text-gray-500 text-sm mt-1">
          Historical federal contract awards from USAspending.gov. Research recipients, agencies, and award sizes.
        </p>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
          Historical data only — past awards do not predict future awards or guarantee current eligibility.
        </p>
      </div>

      {/* Search + filter bar */}
      <form onSubmit={applyFilters} className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search award descriptions…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showFilters ? "bg-amber-50 border-amber-400 text-amber-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasFilters && <span className="bg-amber-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
          </button>
          <button type="submit" className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors">
            Search
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Recipient name</label>
              <input type="text" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Company name" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Awarding agency</label>
              <input type="text" value={agency} onChange={(e) => setAgency(e.target.value)} placeholder="e.g. Army Corps" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">NAICS code</label>
              <input type="text" value={naics} onChange={(e) => setNaics(e.target.value)} placeholder="238210" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State (pop)</label>
              <input type="text" value={popState} onChange={(e) => setPopState(e.target.value.toUpperCase())} maxLength={2} placeholder="CA" className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </div>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="text-xs text-gray-500 underline col-span-2 sm:col-span-4 text-left">
                Clear all filters
              </button>
            )}
          </div>
        )}
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 mb-6">{error}</div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      )}

      {data && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            {data.total.toLocaleString()} {data.total === 1 ? "award" : "awards"} imported
            {hasFilters ? " matching your filters" : ""}
          </p>

          {data.awards.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <p className="text-gray-500 text-sm">No awards found.</p>
              {!hasFilters && (
                <p className="text-gray-400 text-xs mt-2">Run a USAspending import from the admin dashboard to populate this section.</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {data.awards.map((award) => (
                <Link
                  key={award.id}
                  href={`/federal/awards/${award.id}`}
                  className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-amber-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {award.awardType && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                            {award.awardType}
                          </span>
                        )}
                        {award.naicsCode && (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500">
                            <Tag className="w-3 h-3" /> {award.naicsCode}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2">
                        {award.description || "Contract Award"}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                        {award.recipient && (
                          <span className="font-medium text-gray-700">{award.recipient.name}</span>
                        )}
                        {award.awardingAgencyName && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {award.awardingAgencyName}
                          </span>
                        )}
                        {(award.popCity || award.popState) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[award.popCity, award.popState].filter(Boolean).join(", ")}
                          </span>
                        )}
                        <span>{fmtDate(award.startDate)}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="text-[10px] text-gray-400 mb-0.5">Obligated</p>
                      <p className="text-sm font-bold text-gray-900 flex items-center gap-0.5">
                        <DollarSign className="w-3 h-3 text-green-600" />
                        {fmtMoney(award.obligatedAmount).replace("$", "")}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {data.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-gray-300 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Previous
              </button>
              <span className="text-sm text-gray-500">Page {data.page} of {data.totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:border-gray-300 transition-colors">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
